import type { Block } from './schemas.js';
import type { BlockType } from '../constants.js';

// Default props for each block type. Caller assigns the `id`
// (dashboard uses `ulid`). The picker popover inserts a block with
// these props; the user then edits inline (3B-3b) or via the
// inspector panel (3B-3d).
//
// Placeholder copy is deliberately concrete enough that an empty
// block on the canvas reads as a real section, not a config slot.
// Placeholder image URLs use `placehold.co` so the renderer
// validates the URL until the user uploads their own image
// (image upload lands in 3B-3d).

const PLACEHOLDER_IMAGE = 'https://placehold.co/1200x800?text=Image';
const PLACEHOLDER_AVATAR = 'https://placehold.co/200x200?text=Photo';

type DefaultsMap = {
  [K in BlockType]: Extract<Block, { type: K }>['props'];
};

export const blockDefaults: DefaultsMap = {
  hero: {
    headline: 'A headline that introduces your business.',
    subhead: 'One sentence that tells visitors what you offer and why it matters.',
    layout: 'centered',
    primaryCta: { label: 'Get in touch', href: '#contact' },
  },
  services: {
    title: 'What we offer',
    items: [
      {
        title: 'First service',
        description: 'A short description of what this service includes.',
        price: 'From $60',
      },
      {
        title: 'Second service',
        description: 'A short description of what this service includes.',
        price: 'From $120',
      },
      {
        title: 'Third service',
        description: 'A short description of what this service includes.',
        price: 'From $200',
      },
    ],
    columns: 3,
  },
  pricing: {
    title: 'Packages built around what you actually need',
    subtitle: 'Pick the package that fits the job. Custom requests welcome — just ask.',
    columns: 3,
    tiers: [
      {
        name: 'Essential',
        price: '$120',
        description: 'Our most-booked package for everyday jobs.',
        features: [
          'Everything you need to get started',
          'Up to 2 hours of work',
          'All materials included',
          'Same-week scheduling',
        ],
        cta: { label: 'Book this package', href: '#contact' },
        highlighted: false,
      },
      {
        name: 'Most popular',
        price: '$220',
        description: 'The package most customers go with.',
        features: [
          'Everything in Essential',
          'Up to 4 hours of work',
          'Priority scheduling',
          '30-day satisfaction guarantee',
          'One free touch-up visit',
        ],
        cta: { label: 'Book this package', href: '#contact' },
        highlighted: true,
      },
      {
        name: 'Complete',
        price: '$340',
        description: 'For larger jobs that need the full treatment.',
        features: [
          'Everything in Most popular',
          'Up to 8 hours of work',
          'Premium materials',
          'Detailed before / after report',
          'Two free touch-up visits',
        ],
        cta: { label: 'Book this package', href: '#contact' },
        highlighted: false,
      },
    ],
  },
  gallery: {
    title: 'Recent work',
    images: [
      { url: PLACEHOLDER_IMAGE, alt: '' },
      { url: PLACEHOLDER_IMAGE, alt: '' },
      { url: PLACEHOLDER_IMAGE, alt: '' },
    ],
    layout: 'grid',
  },
  contact: {
    title: 'Get in touch',
    description: 'Tell us how to reach you and we will respond within one business day.',
    formEnabled: true,
  },
  about: {
    title: 'About us',
    body: 'Tell visitors who you are, who you serve, and what makes your work different. Two to four sentences is the sweet spot.',
  },
  testimonials: {
    title: 'What customers say',
    items: [
      {
        quote: 'A short, specific compliment from a happy customer.',
        author: 'Customer name',
        role: 'Their role or company',
      },
    ],
  },
  cta: {
    headline: 'Ready to get started?',
    subhead: 'One line that nudges visitors to take the next step.',
    primaryCta: { label: 'Get in touch', href: '#contact' },
    background: 'brand',
  },
  heading: {
    text: 'Section heading',
    level: 2,
    align: 'left',
  },
  text: {
    body: 'Click here to add a paragraph of text. Keep sentences short so visitors can skim.',
    align: 'left',
  },
  faq: {
    title: 'Frequently asked',
    items: [
      {
        question: 'How can I get started?',
        answer: 'Reach out through the contact form and we will be in touch within one business day.',
      },
      {
        question: 'What does pricing look like?',
        answer: 'We tailor pricing to each project. Share what you need and we will send a quote.',
      },
    ],
  },
  team: {
    title: 'The team',
    members: [
      {
        name: 'Team member name',
        role: 'Role or title',
        bio: 'One sentence about what they do.',
        photo: { url: PLACEHOLDER_AVATAR, alt: '' },
      },
    ],
  },
  credentials: {
    items: [
      { icon: '🛡️', label: 'Licensed & Insured' },
      { icon: '⭐', label: '4.9 on Google' },
      { icon: '📍', label: 'Serving San Diego since 2010' },
      { icon: '🤝', label: 'Family-owned' },
    ],
  },
  locations: {
    heading: 'Our locations',
    showPhones: true,
  },
  footer: {
    businessName: 'Your business name',
    tagline: 'A short, memorable tagline.',
    links: [],
    socialLinks: [],
  },
};

// Human-friendly labels + one-line descriptions for the picker UI.
// Categories drive the grouping in the picker popover.
export type BlockCategory = 'structure' | 'showcase' | 'information';

export interface BlockTypeMeta {
  type: BlockType;
  label: string;
  description: string;
  category: BlockCategory;
}

export const blockTypeMeta: readonly BlockTypeMeta[] = [
  { type: 'hero', label: 'Hero', description: 'Big intro at the top of the page.', category: 'structure' },
  { type: 'heading', label: 'Heading', description: 'A standalone section heading.', category: 'structure' },
  { type: 'text', label: 'Paragraph', description: 'A block of prose.', category: 'structure' },
  { type: 'cta', label: 'Call to action', description: 'Sage panel that nudges the next step.', category: 'structure' },
  { type: 'footer', label: 'Footer', description: 'Closing band with business name + links.', category: 'structure' },

  { type: 'services', label: 'Services', description: 'Grid of what you offer.', category: 'showcase' },
  { type: 'pricing', label: 'Pricing table', description: 'Tiered pricing with features and CTAs.', category: 'showcase' },
  { type: 'gallery', label: 'Gallery', description: 'Grid of photos of your work.', category: 'showcase' },
  { type: 'team', label: 'Team', description: 'People behind the business.', category: 'showcase' },
  { type: 'testimonials', label: 'Testimonials', description: 'Quotes from happy customers.', category: 'showcase' },

  { type: 'about', label: 'About', description: 'Who you are and who you serve.', category: 'information' },
  { type: 'credentials', label: 'Trust strip', description: 'License + reviews + years in business chips.', category: 'information' },
  { type: 'contact', label: 'Contact', description: 'How to reach you.', category: 'information' },
  { type: 'faq', label: 'FAQ', description: 'Questions you hear often.', category: 'information' },
  // Multi-location phase, Task 9 — Scenario B. Only offered to sites whose org
  // has a grouped location with 2+ members; the dashboard picker filters this
  // out via PageDetail.locationsBlockEligible before this meta entry is even
  // shown (see apps/dashboard/web/src/routes/page-edit.tsx).
  { type: 'locations', label: 'Locations', description: 'List of your other locations, pulled from their business info.', category: 'information' },
];

export const blockCategoryLabel: Record<BlockCategory, string> = {
  structure: 'Page structure',
  showcase: 'Showcase',
  information: 'Information',
};
